import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { api } from "@utils/api";
import { showSuccessNotification } from "@utils/showNotification";

import { appointmentQKeys } from "@hooks/queries/appointment/queryKeys";
import { patientQKeys } from "@hooks/queries/patient/queryKeys";
import { useQueryErrorHandling } from "@hooks/useQueryErrorHandling";


interface MutationProps {
  formData: FormData;
}

export const useCreateAppointment = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const handleErrors = useQueryErrorHandling();

  return useMutation({
    mutationFn: async ({ formData }: MutationProps) => {
      if (["teleconsultation", "homecare"].includes(String(formData.get("category")))) {
        const details = JSON.parse(String(formData.get("details")));
        const { data: quote } = await api.post("appointments/manual/price-quote", {
          doctor_id: formData.get("doctor_assigned"),
          service_id: details.t_service_type_id || details.service_type_id,
          patient_country: formData.get("pricing_country") || null,
          partner_id: formData.get("partner_assigned") || null,
        });
        if (quote.enabled) {
          if (!window.confirm(t("confirm-country-price", "Confirm appointment price: {{amount}} XOF ({{country}})?", {
            amount: quote.amount, country: quote.country || t("default-price", "Default price"),
          }))) throw new Error(t("price-not-confirmed", "Appointment price was not confirmed"));
          formData.set("pricing_quote", quote.quote_token);
        }
      }
      formData.delete("pricing_country");
      return api.post(`appointments/manual/multipart`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    },
    onSuccess: () => {
      showSuccessNotification({ message: "create-appointment-success" });
      queryClient.invalidateQueries({ queryKey: patientQKeys.all() });
      queryClient.invalidateQueries({ queryKey: appointmentQKeys.all() });
    },

    onError: (res: any) => {
      console.error("[From Create Appointment]", res);
      queryClient.invalidateQueries({ queryKey: appointmentQKeys.all() });

      handleErrors(res, "mutation");
    },
  });
};
